import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { api } from "@/lib/api";
import { LogOut, Trophy, Calendar, User as UserIcon, Shield, Bell, Star, Menu, X, Sun, Moon, HelpCircle } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/notifications/mine");
        if (active) setUnread(data.filter(n => !n.read).length);
      } catch (err) {
        // ignore polling errors
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 20000);
    return () => { active = false; clearInterval(id); };
  }, [user]);

  const showHelp = () => {
    try { sessionStorage.setItem("mm_show_onboarding", "1"); } catch (e) { /* ignore */ }
    window.location.reload();
  };

  const linkClass = ({ isActive }) =>
    `text-xs tracking-[0.2em] uppercase transition-colors ${
      isActive ? "text-lavender" : "text-white/60 hover:text-white"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `block text-base tracking-[0.15em] uppercase py-3 transition-colors ${
      isActive ? "text-lavender" : "text-white/70 hover:text-white"
    }`;

  const NAV_ITEMS = [
    { to: "/matches", l: "Matches", Icon: Calendar, testid: "nav-matches" },
    { to: "/bonus", l: "Bonus", Icon: Star, testid: "nav-bonus" },
    { to: "/leaderboard", l: "Leaderboard", Icon: Trophy, testid: "nav-leaderboard" },
    { to: "/profile", l: "Profile", Icon: UserIcon, testid: "nav-profile" },
  ];

  return (
    <header className={`sticky top-0 z-50 backdrop-blur-2xl bg-ink/70 border-b ${user?.is_admin ? "border-gold/30" : "border-white/5"}`}>
      <div className="max-w-screen-2xl mx-auto px-4 md:px-10 py-3 md:py-4 flex items-center justify-between gap-3">
        <Link to="/matches" data-testid="logo-link" className="flex items-center gap-2 md:gap-3 group min-w-0">
          <div className={`relative w-9 h-9 md:w-10 md:h-10 rounded-xl grid place-items-center transition-transform group-hover:rotate-6 shrink-0 ${
            user?.is_admin
              ? "bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 shadow-[0_0_24px_rgba(255,215,0,0.5)]"
              : "bg-gradient-to-br from-vivid via-purple to-plum shadow-[0_0_24px_rgba(160,64,192,0.45)]"
          }`}>
            <span className={`font-display text-xl md:text-2xl leading-none mt-0.5 ${user?.is_admin ? "text-plum" : "text-white"}`}>M</span>
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-display text-xl md:text-2xl tracking-[0.08em] flex items-center gap-1.5 md:gap-2 flex-wrap">
              <span className="truncate">MOMENTUM</span>
              {user?.is_admin && (
                <span data-testid="admin-mode-badge" className="text-[9px] tracking-[0.3em] uppercase px-1.5 py-0.5 rounded bg-gold/20 text-gold border border-gold/50 font-sans whitespace-nowrap">
                  Admin
                </span>
              )}
            </div>
            <div className={`hidden md:block text-[9px] tracking-[0.35em] text-white/40 mt-1 ${user?.is_admin ? "" : "-mt-0.5"}`}>FIFA · 2026 · PREDICTOR</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {NAV_ITEMS.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkClass} data-testid={n.testid}>
              <span className="inline-flex items-center gap-2"><n.Icon size={14}/> {n.l}</span>
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin">
              <span className="inline-flex items-center gap-2"><Shield size={14}/> Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button onClick={toggle} data-testid="theme-toggle" title={theme === "dark" ? "Light mode" : "Dark mode"}
            className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-white/10 grid place-items-center hover:border-lavender hover:text-lavender transition-colors">
            {theme === "dark" ? <Sun size={15}/> : <Moon size={15}/>}
          </button>
          <button onClick={showHelp} data-testid="help-btn" title="How to play"
            className="hidden sm:grid w-10 h-10 rounded-full border border-white/10 place-items-center hover:border-lavender hover:text-lavender transition-colors">
            <HelpCircle size={15}/>
          </button>
          <Link to="/notifications" data-testid="bell-btn" className="relative w-9 h-9 md:w-10 md:h-10 rounded-full border border-white/10 grid place-items-center hover:border-lavender hover:text-lavender transition-colors">
            <Bell size={15}/>
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-lavender text-plum font-bold text-[10px] grid place-items-center" data-testid="bell-badge">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </Link>
          {user && (
            <div className="hidden md:flex flex-col items-end leading-tight">
              <div className="text-sm font-semibold truncate max-w-[140px]">{user.name}</div>
              <div className="text-[10px] tracking-widest uppercase text-lavender truncate max-w-[140px]">@{user.league_name}</div>
            </div>
          )}
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" title="Logout"
            className="hidden sm:grid w-10 h-10 rounded-full border border-white/10 place-items-center hover:border-lavender hover:text-lavender transition-colors">
            <LogOut size={15}/>
          </button>
          {/* Mobile menu trigger */}
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open"
            className="lg:hidden w-9 h-9 rounded-full border border-white/10 grid place-items-center hover:border-lavender hover:text-lavender transition-colors">
            <Menu size={16}/>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[100] bg-ink/95 backdrop-blur-xl flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
            <div className="font-display text-2xl tracking-[0.08em]">MOMENTUM</div>
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close"
              className="w-10 h-10 rounded-full border border-white/10 grid place-items-center hover:border-lavender hover:text-lavender transition-colors">
              <X size={16}/>
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-6 py-4">
            {NAV_ITEMS.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className={mobileLinkClass} data-testid={`m-${n.testid}`}>
                <span className="inline-flex items-center gap-3"><n.Icon size={18}/> {n.l}</span>
              </NavLink>
            ))}
            {user?.is_admin && (
              <NavLink to="/admin" onClick={() => setMobileOpen(false)} className={mobileLinkClass} data-testid="m-nav-admin">
                <span className="inline-flex items-center gap-3"><Shield size={18}/> Admin</span>
              </NavLink>
            )}
            <button onClick={() => { setMobileOpen(false); showHelp(); }} data-testid="m-help-btn" className="block w-full text-left text-base tracking-[0.15em] uppercase py-3 text-white/70 hover:text-white">
              <span className="inline-flex items-center gap-3"><HelpCircle size={18}/> How to play</span>
            </button>
          </nav>
          {user && (
            <div className="border-t border-white/5 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">{user.name}</div>
                <div className="text-[10px] tracking-widest uppercase text-lavender">@{user.league_name}</div>
              </div>
              <button onClick={() => { setMobileOpen(false); logout(); navigate("/"); }} data-testid="m-logout-btn"
                className="w-10 h-10 rounded-full border border-white/10 grid place-items-center hover:border-lavender hover:text-lavender transition-colors">
                <LogOut size={15}/>
              </button>
            </div>
          )}
          <div className="border-t border-white/5 px-6 py-3 text-[10px] text-white/50 tracking-wider text-center" data-testid="m-support-contact">
            Support · <a href="mailto:abasit@mcorp.ae?cc=Customer_Operations_Team_Igaming@mcorp.ae" className="text-lavender">abasit@mcorp.ae</a>
          </div>
        </div>
      )}
    </header>
  );
}
